import { useEffect, useState } from "react";
import { DataGrid, GridColDef, GridPaginationModel } from "@mui/x-data-grid";
import { Dialog, DialogContent, DialogTitle, IconButton } from "@mui/material";
import { gridSx, LoadingOverlay, useNewLeadsContext } from "../NewLeads";
import {
  approveCredit,
  denyCredit,
  getDisputedLeads,
} from "../../../services/newLeads";
import moment from "moment";
import { toast } from "react-toastify";

const PAGE_SIZE = 10;

const DetailRow = ({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) => (
  <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
    <span
      style={{
        fontSize: 11,
        fontWeight: 600,
        color: "#9ca3af",
        textTransform: "uppercase",
        letterSpacing: "0.05em",
      }}
    >
      {label}
    </span>
    <span style={{ fontSize: 13, color: "#111827", fontWeight: 500 }}>
      {value || "--"}
    </span>
  </div>
);

const historyIconMap: Record<
  string,
  { icon: string; color: string; bg: string }
> = {
  call: { icon: "bi bi-telephone-fill", color: "#0284c7", bg: "#e0f2fe" },
  message: { icon: "bi bi-chat-dots-fill", color: "#7c3aed", bg: "#ede9fe" },
  default: { icon: "bi bi-check-circle-fill", color: "#16a34a", bg: "#dcfce7" },
};

const callStatusColor: Record<string, string> = {
  completed: "#16a34a",
  "in-progress": "#f59e0b",
  initiated: "#6b7280",
  failed: "#dc2626",
};

const LeadDetailDialog = ({
  lead,
  onClose,
}: {
  lead: any;
  onClose: () => void;
}) => {
  const patientName =
    `${lead?.firstName || ""} ${lead?.lastName || ""}`.trim() || "--";
  const history: any[] = lead?.data || [];

  return (
    <Dialog
      open={!!lead}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{ style: { borderRadius: 10 } }}
    >
      <DialogTitle
        sx={{
          backgroundColor: "#194e5b",
          color: "white",
          borderTopLeftRadius: 10,
          borderTopRightRadius: 10,
          fontSize: 16,
          fontWeight: 600,
          py: 1.5,
          px: 2.5,
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          position: "relative",
        }}
      >
        <div
          style={{
            width: 36,
            height: 36,
            borderRadius: "50%",
            background: "rgba(255,255,255,0.2)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 16,
            fontWeight: 700,
            flexShrink: 0,
          }}
        >
          {patientName.charAt(0).toUpperCase()}
        </div>
        <div>
          <div style={{ fontSize: 15, fontWeight: 700 }}>{patientName}</div>
          <div style={{ fontSize: 11, opacity: 0.75, fontWeight: 400 }}>
            {moment(lead?.createdAt).format("MMM D, YYYY • h:mm a")}
          </div>
        </div>
        <span
          style={{
            marginLeft: "auto",
            background: "rgba(255,255,255,0.2)",
            color: "#fff",
            borderRadius: 6,
            padding: "3px 10px",
            fontSize: 11,
            fontWeight: 600,
            marginRight: 36,
          }}
        >
          {lead?.status}
        </span>
        <IconButton
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 8,
            top: "50%",
            transform: "translateY(-50%)",
            color: "white",
            "&:hover": { backgroundColor: "rgba(255,255,255,0.15)" },
          }}
        >
          <i className="bi bi-x-lg" style={{ fontSize: 14 }} />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 2.5 }}>
        {/* Patient Info */}
        <div
          style={{
            background: "#f9fafb",
            borderRadius: 10,
            padding: 14,
            marginBottom: 12,
            marginTop: 4,
          }}
        >
          <div
            style={{
              fontSize: 11,
              fontWeight: 700,
              color: "#6b7280",
              marginBottom: 12,
              textTransform: "uppercase",
              letterSpacing: "0.06em",
            }}
          >
            <i className="bi bi-person" style={{ marginRight: 6 }} />
            Patient Information
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "12px 24px",
            }}
          >
            <DetailRow label="Full Name" value={patientName} />
            <DetailRow label="Phone" value={lead?.phone} />
            <DetailRow label="Email" value={lead?.email} />
            <DetailRow label="Insurance" value={lead?.insuranceName} />
            <DetailRow label="City" value={lead?.city} />
            <DetailRow label="Postal Code" value={lead?.zipcode} />
            {lead?.formattedAddress && (
              <div style={{ gridColumn: "1 / -1" }}>
                <DetailRow label="Address" value={lead.formattedAddress} />
              </div>
            )}
          </div>
        </div>

        {/* Clinic & Dispute Info */}
        <div
          style={{
            background: "#f9fafb",
            borderRadius: 10,
            padding: 14,
            marginBottom: 12,
          }}
        >
          <div
            style={{
              fontSize: 11,
              fontWeight: 700,
              color: "#6b7280",
              marginBottom: 12,
              textTransform: "uppercase",
              letterSpacing: "0.06em",
            }}
          >
            <i className="bi bi-hospital" style={{ marginRight: 6 }} />
            Clinic & Dispute
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "12px 24px",
            }}
          >
            <DetailRow label="Clinic" value={lead?.clinicName} />
            <DetailRow label="Sub Status" value={lead?.subStatus} />
            <DetailRow label="Resolution" value={lead?.resolution} />
            <DetailRow label="Exception Type" value={lead?.exceptionType} />
            {lead?.clinicAddress && (
              <div style={{ gridColumn: "1 / -1" }}>
                <DetailRow label="Clinic Address" value={lead.clinicAddress} />
              </div>
            )}
          </div>
        </div>

        {/* Activity Timeline */}
        {history.length > 0 && (
          <div style={{ background: "#f9fafb", borderRadius: 10, padding: 14 }}>
            <div
              style={{
                fontSize: 11,
                fontWeight: 700,
                color: "#6b7280",
                marginBottom: 14,
                textTransform: "uppercase",
                letterSpacing: "0.06em",
              }}
            >
              <i className="bi bi-clock-history" style={{ marginRight: 6 }} />
              Activity Timeline ({history.length})
            </div>
            <div style={{ display: "flex", flexDirection: "column" }}>
              {history.map((h: any, i: number) => {
                const meta = historyIconMap[h.type] || historyIconMap.default;
                return (
                  <div key={i} style={{ display: "flex", gap: 12 }}>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        flexShrink: 0,
                      }}
                    >
                      <div
                        style={{
                          width: 28,
                          height: 28,
                          borderRadius: "50%",
                          background: meta.bg,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <i
                          className={meta.icon}
                          style={{ fontSize: 12, color: meta.color }}
                        />
                      </div>
                      {i < history.length - 1 && (
                        <div
                          style={{
                            width: 2,
                            flex: 1,
                            background: "#e5e7eb",
                            margin: "4px 0",
                            minHeight: 12,
                          }}
                        />
                      )}
                    </div>
                    <div
                      style={{
                        paddingBottom: i < history.length - 1 ? 14 : 0,
                        flex: 1,
                        minWidth: 0,
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                          flexWrap: "wrap",
                        }}
                      >
                        <span
                          style={{
                            fontSize: 13,
                            fontWeight: 600,
                            color: "#111827",
                          }}
                        >
                          {h.title || "--"}
                        </span>
                        {h.status && (
                          <span
                            style={{
                              fontSize: 11,
                              fontWeight: 600,
                              color: callStatusColor[h.status] || "#6b7280",
                              background:
                                h.status === "completed"
                                  ? "#dcfce7"
                                  : h.status === "in-progress"
                                  ? "#fef3c7"
                                  : "#f3f4f6",
                              borderRadius: 6,
                              padding: "1px 7px",
                            }}
                          >
                            {h.status}
                          </span>
                        )}
                      </div>
                      {h.description && (
                        <div
                          style={{
                            fontSize: 12,
                            color: "#6b7280",
                            marginTop: 2,
                          }}
                        >
                          {h.description}
                        </div>
                      )}
                      {h.notes && (
                        <div
                          style={{
                            fontSize: 12,
                            color: "#374151",
                            marginTop: 3,
                            fontStyle: "italic",
                          }}
                        >
                          "{h.notes}"
                        </div>
                      )}
                      <div
                        style={{
                          fontSize: 11,
                          color: "#9ca3af",
                          marginTop: 3,
                        }}
                      >
                        {moment(h.date).format("MMM D, YYYY • h:mm a")}
                        {h.callCompletedAt && (
                          <span style={{ marginLeft: 8 }}>
                            → Ended {moment(h.callCompletedAt).format("h:mm a")}
                          </span>
                        )}
                        {h.duration !== undefined &&
                          h.duration !== null &&
                          h.duration !== "" && (
                            <span style={{ marginLeft: 8 }}>
                              · {h.duration}
                            </span>
                          )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export const Disputed = () => {
  const { selectedClinicIds, startDate, endDate } = useNewLeadsContext();

  const [rows, setRows] = useState<any[]>([]);
  const [totalRows, setTotalRows] = useState(0);
  const [loading, setLoading] = useState(false);
  const [reloadTick, setReloadTick] = useState(0);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: PAGE_SIZE,
  });
  const [selectedLead, setSelectedLead] = useState<any>(null);
  const [creditDialog, setCreditDialog] = useState<{
    open: boolean;
    type: "approve" | "deny";
    leadId: string | number | null;
  }>({ open: false, type: "approve", leadId: null });
  const [creditNotes, setCreditNotes] = useState("");
  const [creditSubmitting, setCreditSubmitting] = useState(false);

  const primaryClinicId = selectedClinicIds[0];
  const clinicKey = selectedClinicIds.join(",");

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPaginationModel((prev) => ({ ...prev, page: 0 }));
    }, 500);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    setLoading(true);
    getDisputedLeads({
      page: paginationModel.page + 1,
      record: paginationModel.pageSize,
      clinicId: primaryClinicId || undefined,
      startDate: startDate || undefined,
      endDate: endDate || undefined,
      // backend may ignore if not supported
      search: debouncedSearch || undefined,
    })
      .then((res) => {
        if (res && res.status && !res.status.error) {
          const leads = res.leads || [];
          const mapped = leads.map((l: any, i: number) => ({
            ...l,
            id: l.id ?? l.requestId ?? l.leadId ?? i,
            patientName: `${l.firstName || ""} ${l.lastName || ""}`.trim(),
          }));
          setRows(mapped);
          setTotalRows(res.total ?? leads.length ?? 0);
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [
    paginationModel.page,
    paginationModel.pageSize,
    primaryClinicId,
    clinicKey,
    debouncedSearch,
    startDate,
    endDate,
    reloadTick,
  ]);

  const submitCreditAction = async () => {
    if (!creditDialog.leadId) return;
    if (!creditNotes.trim()) {
      toast.error("Please enter notes.");
      return;
    }
    setCreditSubmitting(true);
    try {
      if (creditDialog.type === "approve") {
        await approveCredit({
          leadId: creditDialog.leadId,
          notes: creditNotes.trim(),
        });
        toast.success("Credit approved.");
      } else {
        await denyCredit({
          leadId: creditDialog.leadId,
          notes: creditNotes.trim(),
        });
        toast.success("Credit denied.");
      }
      setCreditDialog({ open: false, type: "approve", leadId: null });
      setCreditNotes("");
      // refetch disputed list after success
      setReloadTick((t) => t + 1);
    } catch (e: any) {
      toast.error(e?.msg || "Action failed.");
    } finally {
      setCreditSubmitting(false);
    }
  };

  const columns: GridColDef[] = [
    {
      field: "clinicName",
      headerName: "Clinic",
      minWidth: 220,
    },
    {
      field: "patientName",
      headerName: "Patient",
      minWidth: 250,
      renderCell: (params) => (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            lineHeight: 1.2,
            height: "100%",
          }}
        >
          <div style={{ fontWeight: 600, fontSize: 14, color: "#111827" }}>
            {params.row.patientName}
          </div>
          <div style={{ color: "#9ca3af", fontSize: 10 }}>
            {params.row.phone}
          </div>
          <div style={{ color: "#9ca3af", fontSize: 10 }}>
            {params.row.email}
          </div>
        </div>
      ),
    },
    {
      field: "status",
      headerName: "Status",
      minWidth: 130,
      renderCell: (params) => (
        <span
          style={{
            background: "#fee2e2",
            color: "#b91c1c",
            borderRadius: 8,
            padding: "3px 10px",
            fontSize: 12,
            fontWeight: 600,
          }}
        >
          {params.row.status || "--"}
        </span>
      ),
    },
    {
      field: "subStatus",
      headerName: "Sub Status",
      minWidth: 130,
      renderCell: (params) => (
        <span
          style={{
            background: "#dcfce7",
            color: "#16a34a",
            borderRadius: 8,
            padding: "3px 10px",
            fontSize: 12,
            fontWeight: 600,
          }}
        >
          {params.row.subStatus || "--"}
        </span>
      ),
    },
    {
      field: "createdAt",
      headerName: "Time",
      minWidth: 200,
      renderCell: (params) => (
        <span style={{ color: "#374151", fontSize: 13 }}>
          {params.row.createdAt
            ? moment(params.row.createdAt).format("MMM D, YYYY • h:mm a")
            : "--"}
        </span>
      ),
    },
    {
      field: "activity",
      headerName: "Activity",
      minWidth: 110,
      renderCell: (params) => {
        const count = params.row.data?.length ?? 0;
        return (
          <span
            style={{
              background: "#f3f4f6",
              borderRadius: 8,
              padding: "3px 10px",
              fontSize: 12,
              fontWeight: 600,
              color: "#374151",
            }}
          >
            {count} event{count !== 1 ? "s" : ""}
          </span>
        );
      },
    },
    {
      field: "actions",
      headerName: "",
      width: 320,
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            height: "100%",
            gap: 10,
          }}
        >
          <button
            onClick={(e) => {
              e.stopPropagation();
              setCreditDialog({
                open: true,
                type: "approve",
                leadId: params.row.id,
              });
              setCreditNotes("");
            }}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 5,
              border: "1px solid #d1d5db",
              borderRadius: 7,
              background: "#fff",
              cursor: "pointer",
              padding: "5px 12px",
              fontSize: 12,
              fontWeight: 600,
              color: "#374151",
              whiteSpace: "nowrap",
              height: 30,
            }}
          >
            Approve Credit
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              setCreditDialog({
                open: true,
                type: "deny",
                leadId: params.row.id,
              });
              setCreditNotes("");
            }}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 5,
              border: "1px solid #d1d5db",
              borderRadius: 7,
              background: "#fff",
              cursor: "pointer",
              padding: "5px 12px",
              fontSize: 12,
              fontWeight: 600,
              color: "#374151",
              whiteSpace: "nowrap",
              height: 30,
            }}
          >
            Deny Credit
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              setSelectedLead(params.row);
            }}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 5,
              border: "1px solid #d1d5db",
              borderRadius: 7,
              background: "#fff",
              cursor: "pointer",
              padding: "5px 12px",
              fontSize: 12,
              fontWeight: 600,
              color: "#374151",
              whiteSpace: "nowrap",
              height: 30,
            }}
          >
            View
          </button>
        </div>
      ),
    },
  ];

  return (
    <>
      <div
        style={{
          background: "#fff",
          borderRadius: 12,
          border: "1px solid #e5e7eb",
          overflow: "hidden",
          margin: 20,
          position: "relative",
        }}
      >
        {loading && <LoadingOverlay />}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "16px 20px",
            borderBottom: "1px solid #f3f4f6",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ fontWeight: 700, fontSize: 17, color: "#111827" }}>
              Disputed Requests
            </span>
            <span
              style={{
                background: "#fee2e2",
                border: "1px solid #fecaca",
                borderRadius: 12,
                padding: "2px 10px",
                fontSize: 13,
                fontWeight: 600,
                color: "#b91c1c",
              }}
            >
              {totalRows} cases
            </span>
          </div>

          <div style={{ position: "relative" }}>
            <i
              className="bi bi-search"
              style={{
                position: "absolute",
                left: 12,
                top: "50%",
                transform: "translateY(-50%)",
                color: "#9ca3af",
                fontSize: 14,
                pointerEvents: "none",
              }}
            />
            <input
              type="text"
              placeholder="Search disputes..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{
                border: "1px solid #d1d5db",
                borderRadius: 8,
                padding: "9px 14px 9px 36px",
                fontSize: 14,
                color: "#374151",
                background: "#fff",
                width: 280,
                outline: "none",
              }}
            />
          </div>
        </div>

        <DataGrid
          rows={rows}
          columns={columns}
          getRowId={(row) => row.id ?? row.requestId ?? row.leadId}
          disableColumnMenu
          paginationMode="server"
          rowCount={totalRows}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[10, 25, 50]}
          density="standard"
          sx={gridSx}
        />
      </div>

      <LeadDetailDialog
        lead={selectedLead}
        onClose={() => setSelectedLead(null)}
      />

      <Dialog
        open={creditDialog.open}
        onClose={() => {
          if (creditSubmitting) return;
          setCreditDialog({ open: false, type: "approve", leadId: null });
          setCreditNotes("");
        }}
        fullWidth
        maxWidth="sm"
        PaperProps={{ style: { borderRadius: 10 } }}
      >
        <DialogTitle
          sx={{
            fontSize: 16,
            fontWeight: 700,
            px: 2.5,
            py: 1.75,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          {creditDialog.type === "approve" ? "Approve Credit" : "Deny Credit"}
          <IconButton
            onClick={() => {
              if (creditSubmitting) return;
              setCreditDialog({ open: false, type: "approve", leadId: null });
              setCreditNotes("");
            }}
          >
            <i className="bi bi-x-lg" style={{ fontSize: 14 }} />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ px: 2.5, pb: 2.5 }}>
          <div style={{ fontSize: 13, color: "#374151", marginBottom: 8 }}>
            Notes
          </div>
          <textarea
            value={creditNotes}
            onChange={(e) => setCreditNotes(e.target.value)}
            rows={4}
            style={{
              width: "100%",
              border: "1px solid #d1d5db",
              borderRadius: 10,
              padding: 12,
              fontSize: 13,
              outline: "none",
              resize: "vertical",
            }}
          />
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 10,
              marginTop: 14,
            }}
          >
            <button
              onClick={() => {
                if (creditSubmitting) return;
                setCreditDialog({ open: false, type: "approve", leadId: null });
                setCreditNotes("");
              }}
              style={{
                border: "1px solid #d1d5db",
                background: "#fff",
                borderRadius: 10,
                padding: "8px 12px",
                fontSize: 13,
                fontWeight: 700,
                color: "#374151",
                cursor: creditSubmitting ? "not-allowed" : "pointer",
                opacity: creditSubmitting ? 0.6 : 1,
              }}
            >
              Cancel
            </button>
            <button
              onClick={submitCreditAction}
              disabled={creditSubmitting}
              style={{
                border: "1px solid #194e5b",
                background: "#194e5b",
                borderRadius: 10,
                padding: "8px 12px",
                fontSize: 13,
                fontWeight: 700,
                color: "#fff",
                cursor: creditSubmitting ? "not-allowed" : "pointer",
                opacity: creditSubmitting ? 0.7 : 1,
              }}
            >
              {creditSubmitting ? "Submitting..." : "Submit"}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
